import "@/App.css";
import "@/index.css";
import "leaflet/dist/leaflet.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import BottomNav from "@/components/BottomNav";
import TopBar from "@/components/TopBar";
import PushNotifications from "@/components/PushNotifications";
import ServiceWorkerRegister from "@/components/ServiceWorkerRegister";
import InstallPrompt from "@/components/InstallPrompt";
import { Toaster } from "sonner";

import Splash from "@/pages/Splash";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MechanicIntro from "@/pages/MechanicIntro";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import CreateRequest from "@/pages/CreateRequest";
import RequestsList from "@/pages/RequestsList";
import RequestDetail from "@/pages/RequestDetail";
import JobsList from "@/pages/JobsList";
import MapScreen from "@/pages/MapScreen";
import ChatScreen from "@/pages/ChatScreen";
import Profile from "@/pages/Profile";
import Vehicles from "@/pages/Vehicles";
import AdminUsers from "@/pages/AdminUsers";
import AdminCreateMechanic from "@/pages/AdminCreateMechanic";
import AdminJobs from "@/pages/AdminJobs";
import AdminNotifications from "@/pages/AdminNotifications";
import AdminPendingMechanics from "@/pages/AdminPendingMechanics";
import AdminScheduled from "@/pages/AdminScheduled";
import AdminMessages from "@/pages/AdminMessages";
import MechanicMessages from "@/pages/MechanicMessages";
import PaymentSuccess from "@/pages/PaymentSuccess";

function Shell({ children }) {
  return (
    <div className="mobile-shell grain">
      <TopBar />
      {children}
      <BottomNav />
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-[#FF4500] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<Splash />} />
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/mechanic-intro" element={user ? <Navigate to="/app" replace /> : <MechanicIntro />} />
      <Route path="/register" element={user ? <Navigate to="/app" replace /> : <Register />} />
      <Route path="/forgot" element={user ? <Navigate to="/app" replace /> : <ForgotPassword />} />
      <Route path="/reset" element={<ResetPassword />} />

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Shell><Dashboard /></Shell>
          </ProtectedRoute>
        }
      />
      <Route path="/app/request/new" element={<ProtectedRoute roles={["client"]}><Shell><CreateRequest /></Shell></ProtectedRoute>} />
      <Route path="/app/requests" element={<ProtectedRoute roles={["client"]}><Shell><RequestsList /></Shell></ProtectedRoute>} />
      <Route path="/app/request/:id" element={<ProtectedRoute><Shell><RequestDetail /></Shell></ProtectedRoute>} />
      <Route path="/app/jobs" element={<ProtectedRoute roles={["mechanic"]}><Shell><JobsList /></Shell></ProtectedRoute>} />
      <Route path="/app/map" element={<ProtectedRoute><Shell><MapScreen /></Shell></ProtectedRoute>} />
      <Route path="/app/chat/:id" element={<ProtectedRoute><Shell><ChatScreen /></Shell></ProtectedRoute>} />
      <Route path="/app/profile" element={<ProtectedRoute><Shell><Profile /></Shell></ProtectedRoute>} />
      <Route path="/app/vehicles" element={<ProtectedRoute roles={["client"]}><Shell><Vehicles /></Shell></ProtectedRoute>} />
      <Route path="/app/admin/users" element={<ProtectedRoute roles={["admin"]}><Shell><AdminUsers /></Shell></ProtectedRoute>} />
      <Route path="/app/admin/users/new" element={<ProtectedRoute roles={["admin"]}><Shell><AdminCreateMechanic /></Shell></ProtectedRoute>} />
      <Route path="/app/admin/jobs" element={<ProtectedRoute roles={["admin"]}><Shell><AdminJobs /></Shell></ProtectedRoute>} />
      <Route path="/app/admin/pending" element={<ProtectedRoute roles={["admin"]}><Shell><AdminPendingMechanics /></Shell></ProtectedRoute>} />
      <Route path="/app/admin/scheduled" element={<ProtectedRoute roles={["admin"]}><Shell><AdminScheduled /></Shell></ProtectedRoute>} />
      <Route path="/app/admin/notifications" element={<ProtectedRoute roles={["admin"]}><Shell><AdminNotifications /></Shell></ProtectedRoute>} />
      <Route path="/app/admin/messages" element={<ProtectedRoute roles={["admin"]}><Shell><AdminMessages /></Shell></ProtectedRoute>} />
      <Route path="/app/messages" element={<ProtectedRoute roles={["mechanic"]}><Shell><MechanicMessages /></Shell></ProtectedRoute>} />
      <Route path="/app/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <PushNotifications />
        <ServiceWorkerRegister />
        <InstallPrompt />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
